import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  PurchaseSummary,
  useBreakpoint
} from '@cencosud-cencommerce/paris-uikit';

import { useAppSelector } from '@/Application/Hooks/Redux';
import { URL_ROUTES } from '@/constants';
import { cartToCartData } from '@/modules/cart/infrastructure/mappers/cartToCartData';
import { useGetCartTotals } from '@/modules/perseo-billing/hooks';
import { redirectToUrl } from '@/modules/perseo-billing/utils/window';
import { IdentificationDocumentTypes } from '@/modules/shared/types/IdentificationDocumentType';
import { SendCustomerDataEvents } from '@/modules/shared/utils/gtmEvents';
import {
  CustomerDataEventInteractiveActions,
  CustomerDataEventInteractiveCategories,
  CustomerDataEventInteractiveTags,
  CustomerDataEventInteractiveTipoLogin
} from '@/modules/shared/utils/gtmEvents/customerDataTypes';
import { isWeddingFlow } from '@/modules/shared/utils/isWeddingFlow';
import useContinueToShipping from '@/Pages/CustomerDataPage/hooks/useContinueToShipping';
import type { AddressGuestFormValues } from '@/sections/customerData/types/AddressGuestFormValues';
import useCommunesOptions from '@/sections/region/hooks/useCommunesOption';
import useRegionsOptions from '@/sections/region/hooks/useRegionsOptions';

const isWedding = isWeddingFlow();
const { DNI, RUT } = IdentificationDocumentTypes;

export const CustomerDataSummary = () => {
  const cart = useAppSelector((state) => state?.cart?.data);
  const regionOptions = useRegionsOptions();
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const cartTotals = useGetCartTotals();
  const { isMobile } = useBreakpoint();

  const shippingAddress = cart?.shippingAddress;
  const communeOptions = useCommunesOptions(shippingAddress?.region ?? 0);

  const selectedRegion = regionOptions?.find(
    (region) => region.value === shippingAddress?.region
  )?.label;

  const selectedCommune = communeOptions?.find(
    (commune) => commune.value === shippingAddress?.commune
  )?.label;

  const { submissionHandler, isSubmitting } = useContinueToShipping();

  const { otherMethods, cencosudCard, cencoPay } = cartTotals;
  const showGiftCardAppliedPrice = cartTotals?.hasGiftCardApplied;

  const totalPrice = showGiftCardAppliedPrice
    ? otherMethods?.withGiftCard?.price
    : otherMethods?.price;

  const totalPriceCenco = showGiftCardAppliedPrice
    ? otherMethods?.withGiftCard?.price
    : cencosudCard?.price;

  const cpayPrice = cencoPay?.price ?? otherMethods?.price;
  const showTotalPriceCencoPay = isMobile && cpayPrice;
  const totalPriceCencoPay = showTotalPriceCencoPay ? cpayPrice : undefined;

  const isVisibleEditButton = !isWedding;

  const customerForm = useFormContext<AddressGuestFormValues>();
  const { watch } = customerForm;

  const handleContinueClick = async () => {
    SendCustomerDataEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-category':
          CustomerDataEventInteractiveCategories.Checkout,
        'event-interactive-accion': CustomerDataEventInteractiveActions.Click,
        'event-interactive-tag':
          CustomerDataEventInteractiveTags.ContinueWithPayment,
        event: 'event-interactive'
      }
    });

    const email = watch('email');
    const customerRut = watch('customerRut');
    const documentID = watch('documentID');
    const documentType = watch('documentType');

    const rutBase64 = btoa(customerRut ?? '');
    const dniBase64 = btoa(documentID ?? '');

    SendCustomerDataEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-category':
          CustomerDataEventInteractiveCategories.Checkout,
        'event-interactive-accion': CustomerDataEventInteractiveActions.Click,
        'event-interactive-tag':
          CustomerDataEventInteractiveTags.ContinueWithPayment,
        i1: email ?? '',
        i2: customerRut ?? '',
        'event-interactive-rut': documentType === RUT ? rutBase64 : undefined,
        'event-interactive-dni': documentType === DNI ? dniBase64 : undefined,
        'event-interactive-tipoLogin':
          CustomerDataEventInteractiveTipoLogin.Guest,
        'event-interactive-region': selectedRegion ?? '',
        'event-interactive-comuna': selectedCommune ?? '',
        documentType: documentType ?? RUT,
        event: 'event-interactive'
      }
    });

    await submissionHandler();
  };

  const handleOnEditSummary = () => {
    redirectToUrl(URL_ROUTES.CART);

    SendCustomerDataEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-category':
          CustomerDataEventInteractiveCategories.Checkout,
        'event-interactive-accion': CustomerDataEventInteractiveActions.Click,
        'event-interactive-tag': CustomerDataEventInteractiveTags.Edit,
        event: 'event-interactive'
      }
    });
  };

  return (
    <section className="tablet_w:co-pr-4 co-mr-[12px] tablet_w:co-mr-[16px]">
      {cart ? (
        <PurchaseSummary
          isOpen={isSummaryOpen}
          onOpen={() => setIsSummaryOpen(!isSummaryOpen)}
          showCostsOnMobile={{
            showProductsCost: true
          }}
          onContinue={handleContinueClick}
          isVisibleEditButton={isVisibleEditButton}
          onEditCart={handleOnEditSummary}
          cartData={cartToCartData(cart)}
          totalPrice={totalPrice}
          totalPriceCenco={totalPriceCenco}
          totalPriceCencoPay={totalPriceCencoPay}
          continueButtonDisabled={isSubmitting}
        />
      ) : null}
    </section>
  );
};

export default CustomerDataSummary;
